/**
 * Kinly optimistic-action helper
 *
 * Every tap-to-toggle / tap-to-vote / tap-to-mark-done MUST feel instant.
 * Without this, the user taps, sees nothing, taps again, then sees a 10s
 * lag of nothing happening (especially against a cold Railway dyno).
 *
 * USAGE PATTERN
 * -------------
 * Every onClick handler that mutates server state should follow this shape:
 *
 *   const onToggle = (item) =>
 *     optimisticAction({
 *       apply:   () => setItems(arr => arr.map(x => x.id === item.id ? { ...x, done: !x.done } : x)),
 *       undo:    () => setItems(arr => arr.map(x => x.id === item.id ? { ...x, done: x.done }  : x)),
 *       network: () => api.patch(`/.../items/${item.id}`, { done: !item.done }),
 *     });
 *
 * - `apply()` runs SYNCHRONOUSLY before the network call — UI feels instant.
 * - `network()` runs in the background.
 * - `undo()` runs ONLY if the network rejects — state returns to truth.
 *
 * RULES
 * -----
 * 1. NEVER call `load()` / re-fetch a whole list after a single toggle.
 *    The optimistic state IS the truth until proven otherwise.
 * 2. NEVER `await` the api call inside an onClick handler without an
 *    accompanying optimistic state mutation BEFORE the await.
 * 3. Larger tap target: parent rows + checkboxes must be ≥44×44pt (Apple
 *    HIG) when tapped. Use `<button className="tap"` wrapping the row,
 *    not just the icon.
 *
 * EXAMPLES IN THE CODEBASE
 * ------------------------
 * - frontend/src/pages/Plan/TodosView.jsx#toggle           ← parent todo
 * - frontend/src/pages/Plan/TodosView.jsx#toggleChecklistItem  ← sub-item
 * - frontend/src/pages/Plan/ChoresView.jsx#onToggle        ← chore status
 * - frontend/src/pages/PollDetail.jsx#onVote               ← poll vote
 * - frontend/src/components/MedicationDosesCard.jsx#log    ← dose log
 *
 * @param apply  () => void  — synchronously update local state
 * @param undo   () => void  — synchronously revert (only called on failure)
 * @param network () => Promise<any> — the actual server call
 * @returns Promise — resolves with the network result, rejects with the error
 *                    AFTER undo has run.
 */
export async function optimisticAction({ apply, undo, network }) {
  apply();
  try {
    return await network();
  } catch (err) {
    try { undo(); } catch { /* don't mask the original error */ }
    throw err;
  }
}

/**
 * Tiny wrapper for the common "flip a boolean field on an item in an array"
 * pattern. Keeps the call site to a one-liner so devs reach for it.
 *
 *   await optimisticToggleField({
 *     items, setItems, id: item.id, field: 'done', next: !item.done,
 *     network: () => api.patch(`/items/${item.id}`, { done: !item.done }),
 *   });
 */
export function optimisticToggleField({ items, setItems, id, field, next, network }) {
  const prev = items.find((x) => x.id === id)?.[field];
  return optimisticAction({
    apply: () => setItems((arr) => arr.map((x) => (x.id === id ? { ...x, [field]: next } : x))),
    undo:  () => setItems((arr) => arr.map((x) => (x.id === id ? { ...x, [field]: prev } : x))),
    network,
  });
}
